import type { AxiosProgressEvent } from 'axios';
import { upload } from '@/api';
import { toApiError } from '@/library/utils/errors';
import type { Attachment } from '@/library/types/files';

const MAX_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024;

type FileLoader = {
  file: Promise<File | null>;
  uploadTotal: number | null;
  uploaded: number;
};

type FileRepositoryPlugin = {
  createUploadAdapter: (loader: FileLoader) => ImageUploadAdapter;
};

export type ImageUploadAdapterOptions = {
  uploadedImagesGroup: string;
  uploadImagesRequestPath: string;
  onImageUpload?: (file: Attachment) => void;
};

type EditorWithFileRepository = {
  plugins: {
    get: (name: string) => FileRepositoryPlugin;
  };
};

type ImageUploadAdapterOptionsGetter = () => ImageUploadAdapterOptions | null;

class ImageUploadAdapter {
  private abortController = new AbortController();

  constructor(
    private readonly loader: FileLoader,
    private readonly getOptions: ImageUploadAdapterOptionsGetter
  ) {}

  async upload(): Promise<{ default: string }> {
    const options = this.getOptions();
    if (!options) {
      throw new Error('Image upload is not configured');
    }

    const file = await this.loader.file;
    if (!file) {
      throw new Error('No file to upload');
    }

    const genericErrorText = `Couldn't upload file ${file.name}.`;

    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      throw new Error('The image upload failed because the image was too big (max 2MB).');
    }

    const data = new FormData();
    data.append('group', options.uploadedImagesGroup);
    data.append('files[]', file, file.name);

    try {
      const response = await upload<Attachment[]>(options.uploadImagesRequestPath, data, {
        signal: this.abortController.signal,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (typeof evt.total === 'number') {
            this.loader.uploadTotal = evt.total;
          }
          this.loader.uploaded = evt.loaded;
        }
      });

      const uploadedFile = response.data.at(0);
      if (!uploadedFile || !uploadedFile.url) {
        throw new Error(genericErrorText);
      }

      options.onImageUpload?.(uploadedFile);

      return { default: uploadedFile.url };
    } catch (error) {
      throw toApiError(error, genericErrorText);
    }
  }

  abort() {
    this.abortController.abort();
  }
}

export const attachImageUploadAdapter = (
  editor: EditorWithFileRepository,
  getOptions: ImageUploadAdapterOptionsGetter
) => {
  const fileRepository = editor.plugins.get('FileRepository') as unknown as FileRepositoryPlugin;

  fileRepository.createUploadAdapter = loader => new ImageUploadAdapter(loader, getOptions);
};
